import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactPicker from './ContactPicker';
import { clearAvailableServiceTimes } from 'src/store/actions/availableServiceTimes';
import { selectValidation } from 'src/store/selectors/Validation';
import { clearValidation } from 'src/store/actions/validation';
import { clearSuiteUsers } from 'src/store/actions/suites';
import Button from '../../components/Button';
import Callout from 'src/components/InformationCallout/Callout';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import AssignConfirmationReview from './AssignConfirmationReview/AssignConfirmationReview';
import { CanonicalContact } from './ContactPicker/helpers/contactShape';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  AssignConfirmationState,
  useAssignConfirmationState,
} from './CustomHooks/useAssignConfirmationState';
import './ServiceRequestAssignForm.scss';

interface AssignCalloutConfig {
  variant: 'info' | 'warning';
  title: string;
  subtitle: string;
}

interface ServiceRequestAssignFormProps {
  onClose: () => void;
  serviceRequest: any;
  handleStatusChange: (formData: any, serviceRequestId: string) => void;
  from?: string;
  isLegacyTable?: boolean;
  serviceRequestsToUpdate?: IServiceRequest[];
}

const getAssignCalloutConfig = (from: string): AssignCalloutConfig => {
  if (from === 'dashboard') {
    return {
      variant: 'warning',
      title: 'Assigning in bulk',
      subtitle:
        "To support bulk assignment, this list isn't limited to a single property, so you may see people who aren't attached to a property in your current selection. Double-check before assigning.",
    };
  }

  return {
    variant: 'info',
    title: "Looking for someone who's missing?",
    subtitle:
      "Only people assigned to the property this request belongs to are shown here. If someone's missing, assign them to that property first.",
  };
};

export const ServiceRequestAssignForm = ({
  onClose,
  serviceRequest,
  handleStatusChange,
  from,
  isLegacyTable,
  serviceRequestsToUpdate,
}: ServiceRequestAssignFormProps) => {
  const [formData, setFormDataState] = useState<any>({});
  const [selectedContact, setSelectedContact] =
    useState<CanonicalContact | null>(null);

  const confirmation: AssignConfirmationState = useAssignConfirmationState();

  const dispatch = useDispatch();
  const validation = useSelector(selectValidation);

  useEffect(() => {
    dispatch(clearAvailableServiceTimes());
    dispatch(clearSuiteUsers());
  }, []);

  useEffect(() => {
    if (validation.status && validation.message === 'Schedule') {
      onClose();
      dispatch(clearValidation());
    }
  }, [validation, onClose, dispatch]);

  const setFormData = (e: any) => {
    setFormDataState((prev: any) => ({ ...prev, ...e }));
  };

  const handleSubmit = () => {
    handleStatusChange(formData, serviceRequest.id);
    setFormDataState({});
  };

  const requiresBulkConfirmation: boolean = Boolean(
    serviceRequestsToUpdate && serviceRequestsToUpdate.length > 0
  );

  const handleAssignClick = () => {
    if (!requiresBulkConfirmation) {
      handleSubmit();
      return;
    }

    confirmation.onOpenConfirmation();
  };

  const handleConfirmedBulkAssign = () => {
    confirmation.onCancelConfirmation();
    handleSubmit();
  };

  const calloutConfig: AssignCalloutConfig = getAssignCalloutConfig(from);
  const bulkConfirmationMessage: ReactNode = selectedContact ? (
    <>
      You&apos;re assigning <strong>{selectedContact.fullName}</strong> to the
      selected service requests. Review the full list before confirming.
    </>
  ) : (
    'Review the full list of selected service requests before confirming.'
  );

  return (
    <div className="service-request-assign-form">
      <p className="service-request-assign-form-heading">
        Assign a Service Provider, Service Manager or Property Developer.
      </p>

      <div
        className={
          calloutConfig.variant === 'info'
            ? 'service-request-assign-form-callout service-request-assign-form-callout-missing'
            : 'service-request-assign-form-callout'
        }
      >
        <Callout
          variant={calloutConfig.variant}
          title={calloutConfig.title}
          subtitle={calloutConfig.subtitle}
        />
      </div>

      <div className="service-request-assign-form-picker">
        <ContactPicker
          from={from}
          setFormData={setFormData}
          serviceRequest={serviceRequest}
          isLegacyTable={isLegacyTable}
          onSelectedContactChange={(contact: CanonicalContact | null) =>
            setSelectedContact(contact)
          }
        />
      </div>

      <div className="service-request-assign-form-actions">
        <Button
          onClick={handleAssignClick}
          size="small"
          color="teal"
          disabled={!formData.userId}
        >
          Assign
        </Button>
      </div>

      {confirmation.isConfirmationOpen && serviceRequestsToUpdate && (
        <ConfirmationModal
          title="Confirm bulk assignment"
          message={bulkConfirmationMessage}
          warningMessage="Current assignees will be replaced. There's no undo, but you can reassign the requests as needed."
          confirmLabel="Assign"
          variant="warning"
          requireTypeToConfirm
          confirmationWord="ASSIGN"
          className="assign-confirmation-modal"
          onConfirm={handleConfirmedBulkAssign}
          onCancel={confirmation.onCancelConfirmation}
        >
          <AssignConfirmationReview
            serviceRequests={serviceRequestsToUpdate}
            selectedContact={selectedContact}
          />
        </ConfirmationModal>
      )}
    </div>
  );
};

export default ServiceRequestAssignForm;
